// SPDX-FileCopyrightText: Copyright 2025 OP Financial Group (https://op.fi). All Rights Reserved.
//
// SPDX-License-Identifier: LicenseRef-OpAllRightsReserved

import message from './messages';
import { pageRoutes, returnLink } from './Constants';
import { sortByAgreementType } from './util/Util';
import {
    ContractData,
    AdminAgreementType,
    Role,
    AdminData,
    AdminDataBackend,
    AdditionalSignatoryData,
    CompanyDataBackend,
} from './context/ContextWrapper';
import client from './api/RestApi';

const getAdmins = (adminData: AdminData[]): AdminDataBackend[] => {
    const administrators: AdminDataBackend[] = [];
    adminData.forEach((admin) => {
        const newAdmin: AdminDataBackend = {
            firstName: admin.firstName,
            lastName: admin.lastName,
            personalIdentityCode: admin.personalIdentityCode,
            adminPhoneNumber: admin.adminPhoneNumber,
            adminEmailAddress: admin.adminEmailAddress,
            canEditRoles: admin.canEditRoles,
            adminAgreementTypes: getAdminAgreements(admin),
        };
        administrators.push(newAdmin);
    });

    return administrators;
};

const getAdminAgreements = (admin: AdminData): AdminAgreementType[] => {
    return Object.keys(admin.agreementTypes)
        .sort(sortByAgreementType)
        .map((agreementType) => {
            return {
                roles: getRoles(admin.agreementTypes[agreementType].rolesData),
                allRolesSelected: isAllRolesSelected(admin.agreementTypes[agreementType].rolesData, agreementType),
                agreementType: agreementType,
            };
        });
};

const isAllRolesSelected = (rolesData: AdminAgreementType, agreementType: string): boolean => {
    let selectedRoles = rolesData.roles.filter((role) => role.isChecked);
    if (selectedRoles.length > 0) {
        let allGroupRoles = rolesData.roles.filter((role) => role.roleGroup.includes(agreementType));

        return selectedRoles.length === allGroupRoles.length;
    }

    return false;
};

const getRoles = (rolesData: AdminAgreementType): Role[] => {
    return rolesData.roles.map((role) => {
        return {
            name: role.name,
            heading: role.heading,
            isChecked: role.isChecked,
            roleGroup: role.roleGroup,
        };
    });
};

const buildCompanyData = (contractData: ContractData, isAdminFlow: boolean): Partial<CompanyDataBackend> => {
    const companyData = contractData.companyData;
    const adminData = contractData.administrators;
    const confirmSignatory = contractData.confirmSignatory;
    const baseData: Partial<CompanyDataBackend> = {
        agreementType:
            confirmSignatory.selectedSigning === 'tupas' ? 'ONLINE_SIGNED_AGREEMENT' : 'MANUALLY_SIGNED_AGREEMENT',
        businessId: companyData.businessId,
        companyName: companyData.companyName,
        administrators: getAdmins(adminData),
        acceptTerms: confirmSignatory.acceptTerms,
    };
    if (confirmSignatory.selectedSigning === 'tupas') {
        baseData.additionalSignatorySelection = contractData.additionalSignatorySelection;
        if (contractData.additionalSignatorySelection === 'yes') {
            baseData.additionalSignatories = contractData.additionalSignatories.map(
                ({ firstName, lastName, phoneNumber, emailAddress }: AdditionalSignatoryData) => ({
                    firstName,
                    lastName,
                    phoneNumber,
                    emailAddress,
                })
            );
        }
    }
    if (!isAdminFlow) {
        Object.assign(baseData, {
            streetAddress: companyData.streetAddress,
            postalCode: companyData.postalCode,
            cityTown: companyData.cityTown,
            phone: companyData.phone,
            isForeignCompany: companyData.isForeignCompany,
            country: companyData.country,
            companyTypes: companyData.companyTypes,
            companyTypeMessages: companyData.companyTypes.map((companyType) => message(companyType)),
        });
    }
    return baseData;
};

const putCompanyInformation = async (
    url: string,
    data: Partial<CompanyDataBackend>,
    setIsDataSavingInProgress: any
): Promise<boolean | void> => {
    setIsDataSavingInProgress(true);
    try {
        let response = await client.put(url, data);
        setIsDataSavingInProgress(false);
        if (response.status !== 'SUCCESS' || response.EMPTY) {
            window.location.hash = pageRoutes.error;
        } else {
            return true;
        }
        // eslint-disable-next-line
    } catch (e) {
        setIsDataSavingInProgress(false);
        window.location.hash = pageRoutes.error;
    }
};

export const saveDataToSession = (
    contractData: ContractData,
    setIsDataSavingInProgress: any
): Promise<boolean | void> => {
    const data = buildCompanyData(contractData, false);
    return putCompanyInformation('/company-information', data, setIsDataSavingInProgress);
};

export const saveAdminFlowDataToSession = (
    contractData: ContractData,
    setIsDataSavingInProgress: any
): Promise<boolean | void> => {
    const data = buildCompanyData(contractData, true);
    return putCompanyInformation('/company-information-admin-flow', data, setIsDataSavingInProgress);
};

export const handleRedirectionTimeout = (): void => {
    invalidateSession();
    if (process.env.NODE_ENV === 'development') {
        window.location.href = '/corporate-customers/insurance/services/web-service-for-corporate-insurances';
    } else {
        window.location.href = returnLink;
    }
};

export const handleWindowBeforeUnload = (): void => {
    if (window.location.hash !== '#/tupasLogin') {
        invalidateSession();
    }
};

export const invalidateSession = (): void => {
    client.delete('/company-information');
};
